/* eslint-disable */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { House, CalendarDays, Megaphone, UserRound } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  serverTimestamp,
  limit,
} from "firebase/firestore";

import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import ParentHeader from "./ParentHeader";
import ParentTabletLayout from "./tablet/ParentTabletLayout";
import ParentBiometricGate from "./ParentBiometricGate";
import { listenForForegroundPush } from "../../services/notificationService";
import {
  ParentAppLockProvider,
  useParentAppLock,
} from "../../contexts/ParentAppLockContext";

import "../../styles/parent/ParentLayout.css";

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeString(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesAudience(item) {
  const audience = normalizeString(item.audience || item.targetAudience || "all");
  return (
    !audience ||
    ["all", "parent", "parents", "family", "families"].includes(audience)
  );
}

function matchesChild(item, activeChild, activeChildId) {
  if (!activeChildId) return true;

  const studentIds = [
    item.studentId,
    item.childId,
    item.linkedStudentId,
    item.studentDocId,
    ...toArray(item.studentIds),
    ...toArray(item.childIds),
    ...toArray(item.linkedStudentIds),
  ]
    .filter(Boolean)
    .map(String);

  const targetId = String(activeChildId || activeChild?.id || "");

  if (studentIds.length && targetId) {
    return studentIds.includes(targetId);
  }

  const year = normalizeString(
    activeChild?.year || activeChild?.yearGroup || activeChild?.className
  );

  const classNames = [
    ...toArray(item.classNames),
    ...toArray(item.years),
    ...toArray(item.yearGroups),
    ...toArray(item.classes),
  ].map(normalizeString);

  if (classNames.length && year) {
    return classNames.includes(year);
  }

  return true;
}


const BIOMETRIC_UNLOCK_SESSION_KEY = "pp_parent_biometric_unlocked_at";
function recentlyUnlockedWithBiometric() {
  try {
    const raw = sessionStorage.getItem(BIOMETRIC_UNLOCK_SESSION_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < 90 * 1000;
  } catch {
    return false;
  }
}

function scrollParentToTop() {
  try {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  } catch {}

  const scrollTargets = [];

  try {
    const main = document.querySelector(".pp-parent-main");
    if (main) scrollTargets.push(main);
  } catch {}

  try {
    scrollTargets.push(window);
  } catch {}

  try {
    scrollTargets.push(document.documentElement, document.body);
  } catch {}

  scrollTargets.filter(Boolean).forEach((target) => {
    try {
      if (target === window) {
        target.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } else if (typeof target.scrollTo === "function") {
        target.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } else {
        target.scrollTop = 0;
        target.scrollLeft = 0;
      }
    } catch {
      try {
        target.scrollTop = 0;
        target.scrollLeft = 0;
      } catch {}
    }
  });
}

function scheduleParentScrollToTop() {
  let cancelled = false;

  const run = () => {
    if (cancelled) return;
    scrollParentToTop();
  };

  run();

  const raf = requestAnimationFrame(run);
  const timer = setTimeout(run, 80);

  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
    clearTimeout(timer);
  };
}


function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildBehaviourUpdate(docId, data, activeChild, schoolId) {
  const categoryLabel =
    data?.categoryLabel ||
    data?.category ||
    data?.type ||
    "Behaviour";

  const points = data?.points || data?.score || "";
  const note = String(data?.note || data?.message || "").trim();
  const studentName = data?.studentName || activeChild?.name || "your child";

  return {
    id: `behaviour-${docId}`,
    sourceId: docId,
    type: "behaviour",
    category: "behaviour",
    audience: "parent",
    title: `${categoryLabel} update`,
    body: note || `${studentName} received a behaviour update${points ? ` (${points} points)` : ""}.`,
    studentId: data?.studentId || activeChild?.id || "",
    studentIds: [data?.studentId || activeChild?.id || ""].filter(Boolean),
    schoolId: data?.schoolId || schoolId,
    createdAt: data?.createdAt || data?.loggedAt || data?.updatedAt || null,
    read: false,
    seen: false,
    isRead: false,
    ...data,
  };
}


function isIPadDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const touchPoints = Number(navigator.maxTouchPoints || 0);

  // Phones must never enter the iPad shell.
  if (/iPhone|iPod/i.test(userAgent)) return false;

  const viewportWidth = Number(
    window.innerWidth || document.documentElement?.clientWidth || 0
  );
  const viewportHeight = Number(
    window.innerHeight || document.documentElement?.clientHeight || 0
  );

  // iPad portrait should use the normal mobile-style parent shell:
  // blue header + bottom nav. The iPad side layout is landscape only.
  const isLandscape = viewportWidth > viewportHeight;
  if (!isLandscape || viewportWidth < 900) return false;

  const screenWidth = Number(window.screen?.width || 0);
  const screenHeight = Number(window.screen?.height || 0);
  const shortestSide = Math.min(screenWidth, screenHeight);
  const longestSide = Math.max(screenWidth, screenHeight);

  const looksLikeIPad =
    /iPad/i.test(userAgent) ||
    (platform === "MacIntel" && touchPoints > 1) ||
    (shortestSide >= 700 && longestSide >= 900);

  return looksLikeIPad;
}

function ParentLayoutInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { biometricEnabled, isUnlocked, checkingLock } = useParentAppLock();
  const [isTabletLayout, setIsTabletLayout] = useState(() => isIPadDevice());

  const [profile, setProfile] = useState(null);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState("");
  const [updates, setUpdates] = useState([]);
  const [behaviourLogs, setBehaviourLogs] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [readUpdateIds, setReadUpdateIds] = useState([]);
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [liveToast, setLiveToast] = useState(null);
  const [hasPrimedUpdates, setHasPrimedUpdates] = useState(false);
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine !== false;
  });


  const childUnsubsRef = useRef([]);
  const updatesUnsubRef = useRef(null);
  const behaviourUnsubRef = useRef(null);
  const attendanceUnsubRef = useRef(null);
  const readsUnsubRef = useRef(null);

  const schoolId = profile?.schoolId || "";

  useEffect(() => {
    if (!schoolId) {
      setSchoolProfile(null);
      return;
    }

    const schoolRef = doc(db, "schools", schoolId);

    const unsubSchool = onSnapshot(
      schoolRef,
      (snap) => {
        if (!snap.exists()) {
          setSchoolProfile(null);
          return;
        }

        const data = snap.data() || {};
        setSchoolProfile({
          id: snap.id,
          ...data,
        });
      },
      (err) => {
        console.warn("Failed loading school profile:", err);
        setSchoolProfile(null);
      }
    );

    return () => {
      try {
        unsubSchool?.();
      } catch {}
    };
  }, [schoolId]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateOnlineState = () => {
      setIsOnline(navigator.onLine !== false);
    };

    updateOnlineState();

    window.addEventListener("online", updateOnlineState);
    window.addEventListener("offline", updateOnlineState);

    return () => {
      window.removeEventListener("online", updateOnlineState);
      window.removeEventListener("offline", updateOnlineState);
    };
  }, []);


  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateDeviceLayout = () => {
      setIsTabletLayout(isIPadDevice());
    };

    updateDeviceLayout();
    window.addEventListener("resize", updateDeviceLayout);
    window.addEventListener("orientationchange", updateDeviceLayout);

    return () => {
      window.removeEventListener("resize", updateDeviceLayout);
      window.removeEventListener("orientationchange", updateDeviceLayout);
    };
  }, []);



  useLayoutEffect(() => {
    return scheduleParentScrollToTop();
  }, [location.pathname, location.search, location.hash, location.key]);


  useEffect(() => {
    if (!user?.uid || profile?.role !== "parent" || !isOnline) return undefined;

    const storageKey = `pp_parent_last_active_write_${user.uid}`;
    const now = Date.now();
    const fifteenMinutes = 15 * 60 * 1000;

    try {
      const previous = Number(localStorage.getItem(storageKey) || 0);
      if (previous && now - previous < fifteenMinutes) return undefined;
      localStorage.setItem(storageKey, String(now));
    } catch {}

    setDoc(
      doc(db, "users", user.uid),
      {
        lastActiveAt: serverTimestamp(),
        engagementStatus: "active",
        lastActiveRoute: location.pathname,
        activityUpdatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      console.warn("Parent activity could not be recorded:", error);
    });

    return undefined;
  }, [user?.uid, profile?.role, isOnline, location.pathname]);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      setChildren([]);
      setActiveChildId("");
      return;
    }

    const clearChildSubs = () => {
      childUnsubsRef.current.forEach((unsub) => {
        try {
          unsub?.();
        } catch {}
      });
      childUnsubsRef.current = [];
    };

    const userRef = doc(db, "users", user.uid);

    const unsubUser = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          setChildren([]);
          setActiveChildId("");
          clearChildSubs();
          return;
        }

        const data = snap.data() || {};
        const nextSchoolId = data.schoolId || "";
        const rawChildren = Array.isArray(data.children) ? data.children : [];

        setProfile({
          uid: user.uid,
          ...data,
        });

        clearChildSubs();

        if (!nextSchoolId || !rawChildren.length) {
          setChildren([]);
          setActiveChildId("");
          return;
        }

        setChildren(
          rawChildren.map((id) => ({
            id,
            name: "Child",
            year: "",
            schoolId: nextSchoolId,
            loading: true,
            denied: false,
          }))
        );

        setActiveChildId((prev) => {
          if (prev && rawChildren.includes(prev)) return prev;
          return rawChildren[0] || "";
        });

        rawChildren.forEach((childId) => {
          const studentRef = doc(db, "schools", nextSchoolId, "students", childId);

          const unsubStudent = onSnapshot(
            studentRef,
            (studentSnap) => {
              const studentData = studentSnap.exists()
                ? studentSnap.data() || {}
                : {};

              setChildren((prev) =>
                prev.map((child) =>
                  child.id === childId
                    ? {
                        ...child,
                        id: childId,
                        schoolId: nextSchoolId,
                        name:
                          studentData.fullName ||
                          studentData.name ||
                          studentData.firstName ||
                          child.name ||
                          "Child",
                        year:
                          studentData.year ||
                          studentData.yearGroup ||
                          studentData.className ||
                          child.year ||
                          "",
                        className: studentData.className || child.className || "",
                        loading: false,
                        denied: false,
                      }
                    : child
                )
              );
            },
            () => {
              setChildren((prev) =>
                prev.map((child) =>
                  child.id === childId
                    ? {
                        ...child,
                        loading: false,
                        denied: true,
                      }
                    : child
                )
              );
            }
          );

          childUnsubsRef.current.push(unsubStudent);
        });
      },
      (err) => {
        console.error("Failed loading parent profile:", err);
      }
    );

    return () => {
      try {
        unsubUser?.();
      } catch {}
      clearChildSubs();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!schoolId) {
      setUpdates([]);
      try {
        updatesUnsubRef.current?.();
      } catch {}
      return;
    }

    try {
      updatesUnsubRef.current?.();
    } catch {}

    const updatesRef = collection(db, "schools", schoolId, "updates");
    const updatesQuery = query(updatesRef, orderBy("createdAt", "desc"), limit(60));

    const unsubUpdates = onSnapshot(
      updatesQuery,
      (snap) => {
        setUpdates(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      },
      (err) => {
        console.error("Failed to subscribe to updates:", err);
        setUpdates([]);
      }
    );

    updatesUnsubRef.current = unsubUpdates;

    return () => {
      try {
        unsubUpdates?.();
      } catch {}
    };
  }, [schoolId]);

    const activeChild = useMemo(() => {
    return children.find((child) => child.id === activeChildId) || null;
  }, [children, activeChildId]);

  useEffect(() => {
    try {
      behaviourUnsubRef.current?.();
    } catch {}

    if (!schoolId || !activeChild?.id || activeChild?.denied) {
      setBehaviourLogs([]);
      return;
    }

    const behaviourRef = collection(db, "schools", schoolId, "behaviourLogs");
    const behaviourQuery = query(
      behaviourRef,
      where("studentId", "==", activeChild.id),
      limit(80)
    );

    const unsubBehaviour = onSnapshot(
      behaviourQuery,
      (snap) => {
        const next = snap.docs
          .map((d) => buildBehaviourUpdate(d.id, d.data() || {}, activeChild, schoolId))
          .filter((item) => item?.studentId && item.notifyParent !== false)
          .sort((a, b) => timestampMillis(b.createdAt) - timestampMillis(a.createdAt));

        setBehaviourLogs(next);
      },
      (err) => {
        console.error("Failed to subscribe to parent behaviour logs:", err);
        setBehaviourLogs([]);
      }
    );

    behaviourUnsubRef.current = unsubBehaviour;

    return () => {
      try {
        unsubBehaviour?.();
      } catch {}
    };
  }, [schoolId, activeChild?.id, activeChild?.denied]);


  useEffect(() => {
    if (!user?.uid) {
      setReadUpdateIds([]);
      try {
        readsUnsubRef.current?.();
      } catch {}
      return;
    }

    try {
      readsUnsubRef.current?.();
    } catch {}

    const readsRef = doc(db, "users", user.uid, "meta", "notificationReads");

    const unsubReads = onSnapshot(
      readsRef,
      (snap) => {
        const data = snap.exists() ? snap.data() || {} : {};
        setReadUpdateIds(Array.isArray(data.readIds) ? data.readIds.map(String) : []);
      },
      (err) => {
        console.error("Failed to subscribe to notification read state:", err);
        setReadUpdateIds([]);
      }
    );

    readsUnsubRef.current = unsubReads;

    return () => {
      try {
        unsubReads?.();
      } catch {}
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!schoolId || !user?.uid || profile?.role !== "parent") {
      setLiveNotifications([]);
      return;
    }

    const notificationsRef = collection(db, "schools", schoolId, "notifications");

    const q = query(
      notificationsRef,
      where("schoolId", "==", schoolId),
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setLiveNotifications(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      },
      (err) => {
        console.warn("Parent notifications disabled for now:", err);
        setLiveNotifications([]);
      }
    );

    return () => {
      try {
        unsub?.();
      } catch {}
    };
  }, [schoolId, user?.uid, profile?.role]);

  useEffect(() => {
    const unsubscribe = listenForForegroundPush((notification) => {
      const route =
        notification?.data?.route || notification?.data?.url || "/parent/updates";

      if (notification?.clicked) {
        navigate(route);
        return;
      }

      setLiveToast({
        id: Date.now(),
        title: notification?.title || "New update",
        body: notification?.body || "",
        route,
      });
    });

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    try {
      attendanceUnsubRef.current?.();
    } catch {}

    if (!schoolId || !activeChild?.id || activeChild?.denied) {
      setAttendanceRecords([]);
      return;
    }

    const attendanceRef = collection(
      db,
      "schools",
      schoolId,
      "students",
      activeChild.id,
      "attendance_daily"
    );

    const attendanceQuery = query(attendanceRef, orderBy("date", "desc"), limit(31));

    const unsubAttendance = onSnapshot(
      attendanceQuery,
      (snap) => {
        setAttendanceRecords(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      },
      () => {
        setAttendanceRecords([]);
      }
    );

    attendanceUnsubRef.current = unsubAttendance;

    return () => {
      try {
        unsubAttendance?.();
      } catch {}
    };
  }, [schoolId, activeChild?.id, activeChild?.denied]);

  useEffect(() => {
    if (!children.length) return;

    const validChildIds = children.filter((c) => !c.denied).map((c) => c.id);

    if (!validChildIds.length) {
      setActiveChildId("");
      return;
    }

    if (!activeChildId || !validChildIds.includes(activeChildId)) {
      setActiveChildId(validChildIds[0]);
    }
  }, [children, activeChildId]);

  const headerTitle = useMemo(() => {
    if (location.pathname.includes("/attendance")) return "Attendance";
    if (location.pathname.includes("/updates")) return "Updates";
    if (location.pathname.includes("/notifications")) return "Notifications";
    if (location.pathname.includes("/profile")) return "Profile";
    if (location.pathname.includes("/report-absence")) return "Absence";
    if (location.pathname.includes("/reports")) return "Reports";
    return "Home";
  }, [location.pathname]);

  const visibleChildOptions = useMemo(() => {
    return children.filter((child) => !child.denied);
  }, [children]);

  const visibleUpdates = useMemo(() => {
    const schoolUpdates = updates
      .filter((item) => matchesAudience(item))
      .filter((item) => matchesChild(item, activeChild, activeChildId));

    return [...schoolUpdates, ...behaviourLogs].sort(
      (a, b) => timestampMillis(b.createdAt || b.publishedAt || b.updatedAt || b.date) -
        timestampMillis(a.createdAt || a.publishedAt || a.updatedAt || a.date)
    );
  }, [updates, behaviourLogs, activeChild, activeChildId]);

  const unreadUpdates = useMemo(() => {
    const readSet = new Set(readUpdateIds.map(String));
    return visibleUpdates.filter((item) => !readSet.has(String(item.id)));
  }, [visibleUpdates, readUpdateIds]);

  const unreadCount = unreadUpdates.length;

  const updatesWithReadState = useMemo(() => {
    const readSet = new Set(readUpdateIds.map(String));

    return visibleUpdates.map((item) => ({
      ...item,
      read: readSet.has(String(item.id)),
      seen: readSet.has(String(item.id)),
      isRead: readSet.has(String(item.id)),
    }));
  }, [visibleUpdates, readUpdateIds]);

  const latestVisibleUpdate = updatesWithReadState[0] || null;

  useEffect(() => {
    if (!visibleUpdates.length) return;

    if (!hasPrimedUpdates) {
      setHasPrimedUpdates(true);
      return;
    }

    const newest = visibleUpdates[0];
    if (!newest?.id) return;

    const isUnread = !readUpdateIds.map(String).includes(String(newest.id));
    if (!isUnread) return;

    setLiveToast({
      id: newest.id,
      title: newest.title || "New update",
      body: newest.body || newest.message || "You have a new school update.",
      route: "/parent/updates",
    });

    const timeout = setTimeout(() => {
      setLiveToast(null);
    }, 4500);

    return () => clearTimeout(timeout);
  }, [visibleUpdates, readUpdateIds, hasPrimedUpdates]);

  const persistReadIds = useCallback(
    async (ids) => {
      if (!user?.uid) return;

      await setDoc(
        doc(db, "users", user.uid, "meta", "notificationReads"),
        {
          readIds: ids.map(String),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [user?.uid]
  );

  const markUpdatesRead = useCallback(
    async (ids = []) => {
      const nextIds = toArray(ids).map(String).filter(Boolean);
      if (!nextIds.length) return;

      const merged = Array.from(new Set([...readUpdateIds, ...nextIds]));
      setReadUpdateIds(merged);

      try {
        await persistReadIds(merged);
      } catch (err) {
        console.error("Failed to persist read notifications:", err);
      }
    },
    [readUpdateIds, persistReadIds]
  );

  const markAllVisibleUpdatesRead = useCallback(async () => {
    const ids = visibleUpdates.map((item) => String(item.id));
    if (!ids.length) return;

    const merged = Array.from(new Set([...readUpdateIds, ...ids]));
    setReadUpdateIds(merged);

    try {
      await persistReadIds(merged);
    } catch (err) {
      console.error("Failed to persist visible read notifications:", err);
    }
  }, [visibleUpdates, readUpdateIds, persistReadIds]);

  const markUpdateUnread = useCallback(
    async (id) => {
      const next = readUpdateIds.filter((x) => String(x) !== String(id));
      setReadUpdateIds(next);

      try {
        await persistReadIds(next);
      } catch (err) {
        console.error("Failed to mark notification unread:", err);
      }
    },
    [readUpdateIds, persistReadIds]
  );

  const markNotificationsRead = useCallback(
    async (ids = []) => {
      const targetIds = toArray(ids).map(String).filter(Boolean);
      if (!targetIds.length || !user?.uid) return;

      const targetSet = new Set(targetIds);

      const targets = liveNotifications.filter((item) => {
        return (
          targetSet.has(String(item.id)) || targetSet.has(String(item.updateId))
        );
      });

      setLiveNotifications((prev) =>
        prev.map((item) =>
          targetSet.has(String(item.id)) || targetSet.has(String(item.updateId))
            ? {
                ...item,
                read: true,
                seen: true,
                readAt: new Date(),
                seenAt: new Date(),
              }
            : item
        )
      );

      const readIds = targets
        .map((item) => String(item.updateId || item.id))
        .filter(Boolean);

      if (!readIds.length) return;

      const merged = Array.from(new Set([...readUpdateIds, ...readIds]));
      setReadUpdateIds(merged);

      try {
        await persistReadIds(merged);
      } catch (err) {
        console.error("Failed to persist notification reads:", err);
      }
    },
    [liveNotifications, readUpdateIds, persistReadIds, user?.uid]
  );

  if (checkingLock) {
    if (Capacitor.isNativePlatform()) return null;

    return (
      <div className="pp-parent-shell pp-parent-shell-loading">
        <div className="pp-route-loading-card" />
      </div>
    );
  }

  if (biometricEnabled && !isUnlocked && !recentlyUnlockedWithBiometric()) {
    return <ParentBiometricGate />;
  }

  const resolvedActiveChild =
    activeChild && !activeChild.denied
      ? activeChild
      : visibleChildOptions[0] || null;

  const resolvedSchoolName =
    profile?.schoolName ||
    profile?.schoolDisplayName ||
    schoolProfile?.schoolName ||
    schoolProfile?.name ||
    schoolProfile?.displayName ||
    schoolProfile?.schoolDisplayName ||
    "";

  const outletContext = {
    profile,
    linkedChildren: visibleChildOptions,
    children: visibleChildOptions,
    activeChild: resolvedActiveChild,
    activeChildId,
    setActiveChild: (child) => {
      const nextId = child?.id || child?.studentId || child?.docId || "";
      if (nextId) setActiveChildId(nextId);
    },
    updates: updatesWithReadState,
    visibleUpdates: updatesWithReadState,
    latestVisibleUpdate,
    unreadUpdates,
    attendanceRecords,
    schoolId,
    schoolName: resolvedSchoolName,
    schoolProfile,
    parentUid: profile?.uid || user?.uid || "",
    unreadCount,
    readUpdateIds,
    liveNotifications,
    isOnline,
    isTabletLayout,
    offlineMessage: "You are offline. Please reconnect and try again.",
    markNotificationsRead,
    markUpdatesRead,
    markAllVisibleUpdatesRead,
    markUpdateUnread,
    markAsRead: (id) => markUpdatesRead([id]),
    markAllAsRead: markAllVisibleUpdatesRead,
  };

  if (isTabletLayout) {
    return (
      <ParentTabletLayout
        outletContext={outletContext}
        childOptions={visibleChildOptions}
        activeChild={resolvedActiveChild}
        activeChildId={activeChildId}
        onChildChange={setActiveChildId}
        unreadCount={unreadCount}
        isOnline={isOnline}
        liveToast={liveToast}
        onToastOpen={(toast) => navigate(toast?.route || "/parent/updates")}
        onToastClose={() => setLiveToast(null)}
      />
    );
  }

  return (
    <div className="pp-parent-shell">
      <ParentHeader
        title={headerTitle}
        childOptions={visibleChildOptions}
        activeChild={resolvedActiveChild}
        activeChildId={activeChildId}
        onChildChange={setActiveChildId}
      />

      {!isOnline ? (
        <div className="pp-offline-banner" role="status" aria-live="polite">
          <span className="pp-offline-banner-icon" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              width="15"
              height="15"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 8.8C7.8 4.4 16.2 4.4 22 8.8" />
              <path d="M5.5 12.3c3.8-2.8 9.2-2.8 13 0" />
              <path d="M9 15.8c1.8-1.2 4.2-1.2 6 0" />
              <path d="M12 19h.01" />
              <path d="M3 3l18 18" />
            </svg>
          </span>
          <span>You are offline. Some information may not be up to date.</span>
        </div>
      ) : null}

      <main id="pp-parent-main-content" className="pp-parent-main" tabIndex={-1}>
        <div className="pp-parent-page-content">
          <div className="pp-parent-route-frame">
            <Outlet context={outletContext} />
          </div>
        </div>
      </main>

      {liveToast ? (
        <button
          type="button"
          className="pp-live-toast"
          aria-label={`${liveToast.title}. ${liveToast.body}. Open update`}
          onClick={() => {
            navigate(liveToast.route || "/parent/updates");
            setLiveToast(null);
          }}
        >
          <div className="pp-live-toast-dot" />
          <div className="pp-live-toast-copy">
            <strong>{liveToast.title}</strong>
            <span>{liveToast.body}</span>
          </div>
        </button>
      ) : null}

      <nav className="pp-parent-bottom-nav" aria-label="Parent navigation">
        <NavItem to="/parent/home" label="Home" icon={<House size={18} />} />
        <NavItem
          to="/parent/attendance"
          label="Attendance"
          icon={<CalendarDays size={18} />}
        />
        <NavItem
          to="/parent/updates"
          label="Updates"
          icon={<Megaphone size={18} />}
          badgeCount={unreadCount}
        />
        <NavItem
          to="/parent/profile"
          label="Profile"
          icon={<UserRound size={18} />}
        />
      </nav>
    </div>
  );
}

export default function ParentLayout() {
  const { user, role, profile } = useAuth();
  const effectiveRole = role || profile?.role || null;

  return (
    <ParentAppLockProvider user={user} role={effectiveRole}>
      <ParentLayoutInner />
    </ParentAppLockProvider>
  );
}

function NavItem({ to, label, icon, badgeCount = 0 }) {
  return (
    <NavLink
      to={to}
      end={to === "/parent/home"}
      style={{ textDecoration: "none" }}
      aria-label={badgeCount > 0 ? `${label}, ${badgeCount > 99 ? "more than 99" : badgeCount} unread` : label}
      className={({ isActive }) =>
        `pp-parent-nav-item ${isActive ? "active" : ""}`
      }
      onClick={() => {
        scheduleParentScrollToTop();
      }}
    >
      <span className="pp-parent-nav-icon" aria-hidden="true">
        {icon}
        {badgeCount > 0 && label === "Updates" ? (
          <span className="pp-parent-nav-badge">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        ) : null}
      </span>
      <span className="pp-parent-nav-label">{label}</span>
    </NavLink>
  );
}
